// Shared helpers + constants for the B2B (Accounts Receivable) tab.
// Mirrors the conventions in finance/Subscriptions.jsx and finance/StaffCost.jsx.

export const CUSTOMER_TYPES = ['Distributor', 'Specialty Retail', 'Export'];
export const CURRENCIES = ['AUD', 'NZD', 'SGD'];
export const ROUTES = ['Vitalus', 'Little Valley', 'Rener', 'SCN', 'Direct'];
export const TERMS = ['Payable Immediately', '7 Days', '14 Days', '15 Days', '30 Days', '30 From EOM'];
export const INVOICE_STATUSES = ['Draft', 'Issued', 'Part-Paid', 'Paid', 'Cancelled'];
export const FULFILMENT = ['Not Sent', 'In Transit', 'Arrived'];
export const CUSTOMER_STATUSES = ['Active', 'Dormant'];
export const ACTIVITY_TYPES = ['draft', 'approval', 'sent', 'reminder', 'payment', 'hold', 'note'];
export const PAYMENT_METHODS = ['Bank Transfer', 'Credit Card', 'PayPal', 'Cash', 'Other'];

// ── money / dates ────────────────────────────────────────────────────────────
const CUR_PREFIX = { AUD: 'A$', NZD: 'NZ$', SGD: 'S$' };
export function fmtMoney(n, cur = 'AUD') {
  const p = CUR_PREFIX[cur] || '$';
  return `${p}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
export function fmtMoney0(n, cur = 'AUD') {
  const p = CUR_PREFIX[cur] || '$';
  return `${p}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
export function fmtDate(d) {
  return d ? new Date(d + (String(d).length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}
export function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
}

// ── FX → AUD (live rates from fx_rates, Subscriptions pattern) ────────────────
export function toAUD(amount, cur, fxMap) {
  const rate = (fxMap && fxMap[cur] != null) ? Number(fxMap[cur]) : 1;
  return Number(amount || 0) * rate;
}

// ── due date from terms (matches the seed generator; "30 From EOM" verified) ──
export function computeDueDate(issueISO, terms) {
  if (!issueISO) return null;
  const d = new Date(issueISO + 'T00:00:00');
  const add = (days) => { const x = new Date(d); x.setDate(x.getDate() + days); return x; };
  let due;
  switch (terms) {
    case 'Payable Immediately': due = d; break;
    case '7 Days': due = add(7); break;
    case '14 Days': due = add(14); break;
    case '15 Days': due = add(15); break;
    case '30 Days': due = add(30); break;
    case '30 From EOM': {
      const eom = new Date(d.getFullYear(), d.getMonth() + 1, 0); // last day of issue month
      eom.setDate(eom.getDate() + 30);
      due = eom; break;
    }
    default: return null;
  }
  return due.toISOString().slice(0, 10);
}

// GST: 10% on AUD (domestic); NZD/SGD are exports → GST-free. Matches the seed.
export function gstSplit(incGst, cur) {
  const inc = Number(incGst || 0);
  if (cur === 'AUD') { const ex = Math.round((inc / 1.1) * 100) / 100; return { ex, gst: Math.round((inc - ex) * 100) / 100 }; }
  return { ex: inc, gst: 0 };
}

// status that a payment should flip an invoice to
export function statusAfterPayment(paidToDate, amountIncGst, currentStatus) {
  const paid = Number(paidToDate || 0);
  const total = Number(amountIncGst || 0);
  if (currentStatus === 'Cancelled' || currentStatus === 'Draft') return currentStatus;
  if (total > 0 && paid >= total - 0.005) return 'Paid';
  if (paid > 0) return 'Part-Paid';
  return currentStatus;
}

// ── colour tokens ────────────────────────────────────────────────────────────
export function statusPill(status, isOverdue) {
  if (isOverdue) return { bg: '#fee2e2', fg: '#dc2626', label: 'Overdue' };
  switch (status) {
    case 'Draft': return { bg: '#f4f4f5', fg: '#52525b', label: 'Draft' };
    case 'Issued': return { bg: '#dbeafe', fg: '#2563eb', label: 'Issued' };
    case 'Part-Paid': return { bg: '#fef3c7', fg: '#d97706', label: 'Part-Paid' };
    case 'Paid': return { bg: '#dcfce7', fg: '#16a34a', label: 'Paid' };
    case 'Cancelled': return { bg: '#f4f4f5', fg: '#a1a1aa', label: 'Cancelled' };
    default: return { bg: '#f4f4f5', fg: '#52525b', label: status || '—' };
  }
}
export const AGEING_COLOR = { 'Current': '#16a34a', '1-30': '#d97706', '31-60': '#ea580c', '60+': '#dc2626' };
export function fulfilmentPill(f) {
  switch (f) {
    case 'Arrived': return { bg: '#dcfce7', fg: '#16a34a' };
    case 'In Transit': return { bg: '#dbeafe', fg: '#2563eb' };
    default: return { bg: '#f4f4f5', fg: '#a1a1aa' };
  }
}
export const TYPE_COLOR = {
  'Distributor': { bg: '#ede9fe', fg: '#7c3aed' },
  'Specialty Retail': { bg: '#e0f2fe', fg: '#0369a1' },
  'Export': { bg: '#fef3c7', fg: '#b45309' },
};
